import type { GroupMember } from '../groups/groupsApi.ts';
import { usePrefersLight } from '../groups/useMemberColour.ts';
import type { Item } from './itemsApi.ts';

interface ItemRowProps {
  item: Item;
  checkedBy: GroupMember | undefined;
  /** Who the item is for, if anyone. Undefined once that person has left the group. */
  assignedTo?: GroupMember | undefined;
  /** Set briefly when someone tried to add this item again; the row shakes. */
  isDuplicate?: boolean;
  onToggle: (item: Item) => void;
  onEdit: (item: Item) => void;

  /**
   * The household's photo for this item's name, if there is one. Only its id: the bytes
   * are fetched when someone actually asks to look, because a list of forty rows must not
   * pull forty photographs down a supermarket connection to draw thumbnails nobody
   * requested.
   */
  photoId?: string | undefined;

  onViewPhoto?: ((photoId: string) => void) | undefined;
}

/**
 * One line of the list.
 *
 * Tapping the row toggles it, and that target is deliberately almost the whole width: on
 * a phone, one-handed, aiming at a small checkbox is the difference between "tap it as
 * you put it in the trolley" and "stop and look at the screen".
 *
 * The edit affordance is a separate, narrow button at the trailing edge — separate
 * because a button inside a button is invalid HTML, and narrow because it must not
 * compete with the toggle. Anything more discoverable (a swipe, a long-press) is either
 * invisible or easy to trigger by accident while scrolling.
 *
 * A checked row is drawn in the colour of whoever checked it, which is what lets two
 * people split a shop and see at a glance who has already got what.
 *
 * <b>Two different colours can apply to one row, and they never both shout.</b>
 * Assignment says who an item is *for*; checking says who actually *got* it. Assignment
 * is drawn as the edge stripe and nothing else, in both states: on a household list most
 * rows are assigned, so a "for Ada" on every one of them was a column of repetition that
 * pushed the names off narrow phones and said the same thing the stripe already said.
 * Checking still takes the tick, the strikethrough and the trailing name, because who
 * bought it is a thing that happened rather than a plan and is the more useful fact.
 *
 * The stripe is therefore the only *visual* carrier of the assignment, but it is still
 * never the only carrier: the assignee's name is in the accessible label in both states,
 * and the editor is where it is read and changed.
 */
export function ItemRow({
  item,
  checkedBy,
  assignedTo,
  isDuplicate = false,
  onToggle,
  onEdit,
  photoId,
  onViewPhoto,
}: ItemRowProps) {
  const prefersLight = usePrefersLight();

  const colourOf = (member: GroupMember | undefined) =>
    member ? (prefersLight ? member.colourOnLight : member.colourHex) : undefined;

  const checkedColour = colourOf(checkedBy);
  const assignedColour = colourOf(assignedTo);

  return (
    <li
      // Addressable so the duplicate guard can scroll it into view — a ticked item sits
      // in the collapsed section and may well be off-screen.
      id={`item-${item.id}`}
      className={[
        'item',
        assignedTo ? 'item--assigned' : '',
        isDuplicate ? 'item--duplicate' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={assignedColour ? { borderInlineStartColor: assignedColour } : undefined}
    >
      <button
        type="button"
        className="item-row"
        onClick={() => onToggle(item)}
        aria-pressed={item.isChecked}
        // Names the people involved, so neither fact is carried by colour alone.
        aria-label={describe(item, checkedBy, assignedTo)}
      >
        <span
          className={item.isChecked ? 'item-row__tick item-row__tick--checked' : 'item-row__tick'}
          style={checkedColour ? { borderColor: checkedColour, backgroundColor: checkedColour } : undefined}
          aria-hidden="true"
        >
          {item.isChecked && (
            <svg viewBox="0 0 16 16" width="14" height="14" focusable="false">
              <path
                d="M3 8.5l3.2 3.2L13 5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </span>

        <span className="item-row__body">
          <span
            className="item-row__name"
            data-checked={item.isChecked || undefined}
            style={checkedColour ? { textDecorationColor: checkedColour } : undefined}
          >
            {item.name}
          </span>
          {item.note && <span className="item-row__note">{item.note}</span>}
        </span>

        {item.isChecked && checkedBy && (
          <span className="item-row__by" style={{ color: checkedColour }}>
            {checkedBy.displayName}
          </span>
        )}

      </button>

      {/* A third sibling, and a sibling for the same reason the pen is: a button inside
          the toggle button would be invalid HTML and would break the one-tap check.
          Nothing here calls stopPropagation, and nothing should need to.

          Shown only on rows that have a photo, so an ordinary list looks exactly as it
          did — and it sits before the pen so the trailing edge stays where the thumb has
          learned to find it.

          Its own accessible name is what carries "this has a photo"; the toggle's label
          is deliberately left alone, because it names the toggle and not the row. */}
      {photoId && onViewPhoto && (
        <button
          type="button"
          className="item-photo"
          onClick={() => onViewPhoto(photoId)}
          aria-label={`Photo of ${item.name}`}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
            <path
              d="M1.5 12S5 5.5 12 5.5 22.5 12 22.5 12 19 18.5 12 18.5 1.5 12 1.5 12z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
          </svg>
        </button>
      )}

      <button
        type="button"
        className="item-edit"
        onClick={() => onEdit(item)}
        aria-label={`Edit ${item.name}`}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" focusable="false">
          <path
            d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </li>
  );
}

/**
 * The row as a screen reader hears it.
 *
 * Both facts are spoken when both apply, because "ticked off by Grace" on an item that
 * was Ada's job is precisely the case where the assignment is worth knowing — and it is
 * the one case the visual rendering deliberately keeps quiet.
 */
function describe(
  item: Item,
  checkedBy: GroupMember | undefined,
  assignedTo: GroupMember | undefined,
): string {
  const parts = [item.name];

  if (item.isChecked && checkedBy) {
    parts.push(`ticked off by ${checkedBy.displayName}`);
  }

  if (assignedTo) {
    parts.push(`for ${assignedTo.displayName}`);
  }

  return parts.join(', ');
}
